import type { FeatureGroupPickedLike } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.compose.utils"
import { contentClassesOf } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.content.utils"
import { useFeatureContentCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.contentCatalog.query"
import { useFeatureSurfaceCatalogQuery } from "@/v2-backoffice-client/license/featureGroup/compose/license.featureGroup.surfaceCatalog.query"
import { useFeatureGroupRuleOptionsQuery } from "@/v2-backoffice-client/license/featureGroup/license.featureGroup.ruleOptions.query"

/**
 * What the two catalogs place: every capability and content code, and the
 * feature types the app enforces. A pick outside `catalogCodes` is an
 * individual feature; a pick of a type outside `entitledTypeCodes` does
 * nothing for a user. Read from the same queries the sections make, so the
 * cache answers.
 */
export function useCatalogPlacement() {
  const surfaceCatalog = useFeatureSurfaceCatalogQuery()
  const contentCatalog = useFeatureContentCatalogQuery()
  const { data: optionData } = useFeatureGroupRuleOptionsQuery()
  const surfaces =
    (surfaceCatalog.data ?? surfaceCatalog.previousData)
      ?.getFeatureSurfaceCatalog ?? []
  const contentItems =
    (contentCatalog.data ?? contentCatalog.previousData)
      ?.getFeatureContentCatalog ?? []
  const options = optionData?.getFeatureGroupRuleOptions ?? []

  return {
    catalogCodes: new Set([
      ...surfaces.map((surface) => surface.Code),
      ...contentItems.map((item) => item.Code),
    ]),
    entitledTypeCodes: new Set(
      contentClassesOf(contentItems, options).map(
        (contentClass) => contentClass.featureTypeCode,
      ),
    ),
  }
}

/** The picks the catalogs do not place. */
export function individualPicksOf(
  features: readonly FeatureGroupPickedLike[],
  catalogCodes: ReadonlySet<string>,
) {
  return features.filter((feature) => !catalogCodes.has(feature.Code))
}
